import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, Subject } from 'rxjs';
import { Area, ChecklistItem, GeographicalRegion, GeographicalRegionService, SubGeographicalRegion, SubGeographicalRegionService } from 'shared-lib';
import { AssignStatus, KycStage, KycSubServiceType } from 'src/app/_modules/customers/_models/kyc-account-data';
import { KycDownloadReportType, ServiceRequestTrackerItem } from 'src/app/models/accountKyc.model';
import { InstallationAndPowerSource } from 'src/app/models/customer.model';
import { OrganizationMultiSelect } from 'src/app/models/organization.model';
import { ProjectDesignationDto } from 'src/app/models/projectDesignation.model';
import { Region } from 'src/app/models/region.model';
import { ProjectType } from 'src/app/models/serviceConnection.model';
import { AreaService } from 'src/app/services/area.service';
import { CustomerService } from 'src/app/services/customer.service';
import { AccountKycService } from 'src/app/services/kyc/account-kyc.service';
import { OrganizationTeamApiService } from 'src/app/services/organization-team-api.service';
import { ProjectDesignationService } from 'src/app/services/project-designation.service';
import { RegionService } from 'src/app/services/region.service';
import { TranslationService } from 'src/app/services/translation.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-service-request-tracker',
  templateUrl: './service-request-tracker.component.html',
  styleUrls: ['./service-request-tracker.component.scss'],
  standalone: false,
})
export class ServiceRequestTrackerComponent implements OnInit, OnDestroy {
  utilityId: number = 0;
  items: ServiceRequestTrackerItem[] = [];

  //search and search type
  searchText: string = '';
  searchFilterApplied: boolean = false;
  searchTextUpdate = new Subject<string>();
  showSelectSearchType: boolean = false;
  selectedSearchType: number;
  selectedSearchTypeDisplay: string;
  searchByOptions = [
    { name: 12, value: 'Application Number', translation: 'COMMON.TABLE_HEADERS.APP_NUM' },
    { name: 14, value: 'Service Order ID', translation: 'METER_REQUEST.SERVICE_ORDER_ID' },
    { name: 13, value: 'MAP ID', translation: 'NEW_SERVICE.MAP_ID' },
    { name: 1, value: 'Account Number', translation: 'COMMON.CUSTOMER.ACCOUNT_NUMBER' },
    { name: 11, value: 'SPN', translation: 'COMMON.CONTRACT_NUMBER' },
    { name: 6, value: 'Email', translation: 'CUSTOMERS.SEARCH_BY.EMAIL' },
  ];
  //search type dropdown position
  ddPos: { top: string; left: string } = { top: '0', left: '0' };

  //regions and areas
  buName: string = this.translationService.getByKeyFromStorage('BU');
  utName: string = this.translationService.getByKeyFromStorage('UT');
  regions: Region[] = [];
  areas: Area[] = [];
  allAreas: Area[] = [];
  selectedAreas: number[] = [];
  selectedRegions: number[] = [];

  hasCimDataIntegration: boolean = false;
  geographicalRegions: (GeographicalRegion & { checked?: boolean })[] = [];
  allSubGeographicalRegions: (SubGeographicalRegion & { checked?: boolean })[] = [];
  subGeographicalRegions: (SubGeographicalRegion & { checked?: boolean })[] = [];
  selectedSubGeographicalRegions: number[] = [];

  //service type
  selectedSltStatus?: number;
  sltOptions: {
    id: number;
    displayValue: string;
    installationAndPowerSource: number;
  }[] = [];
  
  //service request type
  selectedSubserviceType?: number;
  showSubserviceTypes: boolean = false;
  subServiceTypeOptions: { value: number; displayValue: string }[] = [
    { value: KycSubServiceType.PublicLoad, displayValue: 'Public Load' },
    { value: KycSubServiceType.MeterReplacement, displayValue: 'Meter Replacement' },
    { value: KycSubServiceType.Migration, displayValue: 'Migration' },
    { value: KycSubServiceType.Separation, displayValue: 'Separation' },
    { value: KycSubServiceType.PointLoadTransformer, displayValue: 'Point Load Transformer' },
    { value: KycSubServiceType.DedicatedFeeder, displayValue: 'Dedicated Feeder' },
    { value: KycSubServiceType.NewService, displayValue: 'New Service' },
    { value: KycSubServiceType.UpgradeToKcg, displayValue: 'Upgrade to KCG' },
  ];
  
  //project status
  selectedProjectTypes: number[] = [];
  projectTypeOptions: {
    value: number;
    name: string;
    checked: boolean;
  }[] = [
    { value: ProjectType.MAP, name: 'MAP', checked: false },
    { value: ProjectType.NMAP, name: 'NMAP', checked: false },
    { value: ProjectType.KCG, name: 'KCG', checked: false },
  ];

  //project designation
  projectDesignations: ProjectDesignationDto[] = [];
  selectedProjectDesignationIds: number[] = [];

  //tag
  kycTag?: number;
  kycTagOptions = [
    { name: 'Existing', value: 0, translation: 'COMMON.TAG.EXISTING' },
    { name: 'New Customer', value: 1, translation: 'COMMON.TAG.NEW_CUSTOMER' },
  ];

  //application stage
  selectedApplicationStage?: number;
  applicationStageOptions: { value: number; displayValue: string }[] = [
    { value: KycStage.ServiceRequest, displayValue: 'Service Request' },
    { value: KycStage.SiteVerification, displayValue: 'Site Verification' },
    { value: KycStage.ServiceRequestAuthorization, displayValue: 'Service Request Authorization' },
    { value: KycStage.OnboardedCustomers, displayValue: 'Onboarded Customers' }
  ];

  //assigned vendor
  selectedOrganizations: number[] = [];
  organizations: OrganizationMultiSelect[] = [];

  //assignment status
  selectedAssignStatus?: number;
  assignStatusOptions: { value: number; displayValue: string }[] = [
    { value: AssignStatus.Assigned, displayValue: 'Assigned' },
    { value: AssignStatus.NotAssigned, displayValue: 'Not Assigned' },
  ];

  //selected filters cards
  selectedFilters: ChecklistItem[] = [];
  clicked: boolean = false;

  //pagination
  pageSize: number = 10;
  currentPage: number = 1;
  count: number = 0;

  private ngUnsubscribe = new Subject<void>();

  constructor(private toastr: ToastrService,
    private translationService: TranslationService,
    private regionService: RegionService,
    private areaService: AreaService,
    private customerService: CustomerService,
    private projectDesignationService: ProjectDesignationService,
    private organizationTeamService: OrganizationTeamApiService,
    private kycAccountService: AccountKycService,
    private authService: AuthService,
    private geographicalRegionService: GeographicalRegionService,
    private subGeographicalRegionService: SubGeographicalRegionService,
    private router: Router,
    private route: ActivatedRoute
  ){}

  ngOnInit(): void {
    this.utilityId = parseInt(localStorage.getItem('utilityId') || '');

    this.hasCimDataIntegration = this.authService.hasCimDataIntegration;
    if (this.hasCimDataIntegration) {
      this.getGeographicalRegions();
    } else {
      this.getRegions();
    }
    this.getAllSltStatuses();
    this.getProjectDesignations();
    this.getOrganizations();

    this.searchTextUpdate.pipe(debounceTime(500)).subscribe((_) => {
      if (this.searchText == '') this.search();
      this.searchFilterApplied = false;
    });
    this.selectedSearchType = this.selectedSearchType ?? this.searchByOptions[0].name;
    this.selectedSearchTypeDisplay = this.selectedSearchTypeDisplay ?? this.searchByOptions[0].translation;

    document.addEventListener('scroll', this.onAnyScroll, true);

    this.reloadTable();
  }

  private getRegions() {
    this.regionService.getAllAssignedForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data;
          this.getAreas();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getAreas() {
    this.areaService.getAllAssignedForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allAreas = responseData.data;
          this.areas = this.allAreas.filter((area) =>
            this.regions.some(
              (region) => region.id == area.regionId && region.checked,
            ),
          );
          this.createFilterCards();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getGeographicalRegions() {
    this.geographicalRegionService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.geographicalRegions = responseData.data;
          this.getSubGeographicalRegions();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getSubGeographicalRegions() {
    this.subGeographicalRegionService.getAllForMappings().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allSubGeographicalRegions = responseData.data;
          this.subGeographicalRegions = this.allSubGeographicalRegions.filter(
            (sub) =>
              this.geographicalRegions.some(
                (region) =>
                  region.id == sub.geographicalRegion.id && region.checked,
              ),
          );
          this.createFilterCards();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getAllSltStatuses() {
    this.customerService.getAllSltStatuses().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.sltOptions = responseData.data.map((item: any): any => ({
            id: item.id,
            displayValue: item.value,
            installationAndPowerSource:
              InstallationAndPowerSource[
                item.installationAndPowerSource as keyof typeof InstallationAndPowerSource
              ],
          }));
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getProjectDesignations() {
    this.projectDesignationService.getAllForSelect(this.utilityId).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.projectDesignations = responseData.data;
          if (this.selectedProjectDesignationIds.length > 0) {
            (this.projectDesignations as any[]).forEach((pd) => {
              pd.checked = this.selectedProjectDesignationIds.includes(pd.id);
            });
            this.createFilterCards();
          }
        }
      },
      error: (_) => this.toastr.error('Error while fetching project designations'),
    });
  }

  private getOrganizations() {
    this.organizationTeamService.getAllForSelect().subscribe({
      next: (response) => {
        this.organizations = response.data.map(
          (item: any) => new OrganizationMultiSelect(item),
        );
        if(this.selectedOrganizations.length > 0){
          (this.organizations as any[]).forEach((org) => {
            org.checked = this.selectedOrganizations.includes(org.id);
          });
        }
      },
      error: (_) =>
        this.toastr.error('Error occurred while fetching organizations'),
    });
  }

  reloadTable() {
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getServiceRequestTrackerFilterObject(),
      utilityId: this.utilityId,
    };
    this.getAllForServiceRequestTracker(obj);
  }

  private getServiceRequestTrackerFilterObject() {
    return {
      search: this.searchText,
      searchType: this.selectedSearchType,
      selectedAreas: this.selectedAreas.length > 0 ? this.selectedAreas : null,
      selectedRegions: this.selectedRegions.length > 0 ? this.selectedRegions : null,
      selectedSubGeographicalIds: this.selectedSubGeographicalRegions.length > 0 ? this.selectedSubGeographicalRegions : null,
      selectedSltStatus: this.selectedSltStatus ? Number(this.selectedSltStatus) : null,
      selectedSubserviceType: this.selectedSubserviceType ? Number(this.selectedSubserviceType) : null,
      selectedProjectTypes: this.selectedProjectTypes.length > 0 ? this.selectedProjectTypes : null,
      selectedProjectDesignations: this.selectedProjectDesignationIds.length > 0 ? this.selectedProjectDesignationIds : null,
      kycTag: this.kycTag ? Number(this.kycTag) : null,
      kycStage: this.selectedApplicationStage ? Number(this.selectedApplicationStage) : null,
      selectedOrganizations: this.selectedOrganizations.length > 0 ? this.selectedOrganizations : null,
      selectedAssignStatus: this.selectedAssignStatus ? Number(this.selectedAssignStatus) : null,
    };
  }

  download() {
    var obj = this.getServiceRequestTrackerFilterObject();
    (obj as any).kycDownloadReportType = KycDownloadReportType.ServiceRequestTrackerReport;
    this.kycAccountService.download(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  private getAllForServiceRequestTracker(obj: any){
    this.kycAccountService.getAllServiceRequestTracker(obj).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.items = responseData.data.data;
          this.items.map(
            (x) =>
              (x.subserviceType = x.subserviceType.replace(
                /([a-z])([A-Z])/g,
                '$1 $2',
              )),
          );
          this.count = responseData.data.count ?? this.count;
          if (responseData.message != '') {
            this.toastr.warning(responseData.message);
          }
        } else if (response?.status == 204) {
          this.toastr.warning('No content');
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  selectRequestTracker(id: number) {
    this.router.navigate(['service-request-tracker-details', id], {
      relativeTo: this.route,
    });
  }

  search() {
    this.searchFilterApplied = this.searchText ? true : false;
    this.createFilterCards();
  }

  openDropdown(el: HTMLElement): void {
    const wasOpen = this.showSelectSearchType;
    this.showSelectSearchType = false;
    if (!wasOpen) {
      const r = el.getBoundingClientRect();
      const zoom = parseFloat((getComputedStyle(document.documentElement) as any).zoom || '1');
      this.ddPos = { top: (r.bottom / zoom) + 'px', left: (r.left / zoom) + 'px' };
      this.showSelectSearchType = true;
    }
  }

  selectSearchType(searchType: any): void {
    if (this.searchText && this.searchFilterApplied) {
      this.searchText = '';
      this.search();
    }
    this.selectedSearchType = searchType.name;
    this.selectedSearchTypeDisplay = searchType.translation;
    this.showSelectSearchType = false;
  }

  selectRegion() {
    this.areas = this.allAreas.filter((area) =>
      this.regions.some(
        (region) => region.id == area.regionId && region.checked,
      ),
    );
    this.allAreas.forEach((area) => {
      if (!this.areas.some((a) => a.id === area.id)) {
        area.checked = false;
      }
    });
    this.createFilterCards();
  }

  selectGeographicalRegion() {
    this.subGeographicalRegions = this.allSubGeographicalRegions.filter((sub) =>
      this.geographicalRegions.some(
        (region) => region.id == sub.geographicalRegion.id && region.checked,
      ),
    );
    this.allSubGeographicalRegions.forEach((sub) => {
      if (!this.subGeographicalRegions.some((s) => s.id === sub.id)) {
        sub.checked = false;
      }
    });
    this.createFilterCards();
  }

  selectArea() {
    this.createFilterCards();
  }

  selectSltStatus(value: any) {
    if (value != null && value !== '') {
      this.selectedSltStatus = value;
      const isHighCapacity =
        this.sltOptions.find((s) => s.id == this.selectedSltStatus)
          ?.installationAndPowerSource ==
        InstallationAndPowerSource.High_Capacity;
      if (isHighCapacity) {
        this.showSubserviceTypes = true;
      } else {
        this.showSubserviceTypes = false;
        this.selectedSubserviceType = undefined;
      }
    } else {
      this.selectedSltStatus = undefined;
    }
    this.createFilterCards();
  }

  selectSubserviceType(value: any) {
    if (value != null && value !== '') {
      this.selectedSubserviceType = value;
    } else {
      this.selectedSubserviceType = undefined;
    }
    this.createFilterCards();
  }

  selectProjectType(): void {
    this.createFilterCards();
  }

  selectProjectDesignation(): void {
    this.createFilterCards();
  }

  selectKycTag(e: any) {
    if (e.target.value != '') {
      this.kycTag = e.target.value;
    } else {
      this.kycTag = undefined;
    }
    this.createFilterCards();
  }

  selectApplicationStage(value: any) {
    if (value != null && value !== '') {
      this.selectedApplicationStage = value;
    } else {
      this.selectedApplicationStage = undefined;
    }

    this.createFilterCards();
  }

  selectVendor(): void {
    this.createFilterCards();
  }

  selectAssignStatus(value: any) {
    if (value != null && value !== '') {
      this.selectedAssignStatus = value;
    } else {
      this.selectedAssignStatus = undefined;
    }

    this.createFilterCards();
  }

  createFilterCards(): void {
    this.selectedFilters = [];

    if (this.searchText) {
      this.selectedFilters.push(new ChecklistItem('Search', this.searchText, 'COMMON.SEARCH'));
    }

    this.selectedRegions = this.regions.filter((x) => x.checked).map((x) => x.id);
    let selectedAreas = this.areas.filter((x) => x.checked);
    this.selectedAreas = this.areas.filter((x) => x.checked).map((x) => x.id);

    let selectedGeoRegions = this.geographicalRegions.filter((x) => x.checked);
    let selectedSubGeoRegions = this.subGeographicalRegions.filter((x) => x.checked);
    this.selectedSubGeographicalRegions = selectedSubGeoRegions.map((x) => x.id as number);

    for (var i = 0; i < selectedGeoRegions.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          'Geographical Region',
          selectedGeoRegions[i].name,
          'COMMON.GEOGRAPHICAL_REGION.SINGLE',
        ),
      );
    }
    for (var i = 0; i < selectedAreas.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          this.utName,
          selectedAreas[i].name,
          'COMMON.UT.SINGLE',
        ),
      );
    }
    for (var i = 0; i < selectedSubGeoRegions.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          'Sub Geographical Region',
          selectedSubGeoRegions[i].name,
          'COMMON.SUB_GEOGRAPHICAL_REGION.SINGLE',
        ),
      );
    }

    if (this.selectedSltStatus) {
      var sltStatus = this.sltOptions.find(
        (s) => s.id == this.selectedSltStatus,
      );
      this.selectedFilters.push(
        new ChecklistItem('Service Type', sltStatus!.displayValue),
      );
    }

    if (this.selectedSubserviceType) {
      var subServiceType = this.subServiceTypeOptions.find(
        (s) => s.value == this.selectedSubserviceType,
      );
      this.selectedFilters.push(
        new ChecklistItem('Service Request Type', subServiceType!.displayValue),
      );
    }

    let selectedProjectTypes = this.projectTypeOptions.filter((x) => x.checked);
    this.selectedProjectTypes = this.projectTypeOptions.filter((x) => x.checked).map((x) => x.value);
    for (var i = 0; i < selectedProjectTypes.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          'Project Status',
          selectedProjectTypes[i].name,
          'NEW_SERVICE.PROJECT_STATUS',
        ),
      );
    }

    let selectedProjectDesignations = (this.projectDesignations as any[]).filter((x) => x.checked);
    this.selectedProjectDesignationIds = (this.projectDesignations as any[]).filter((x) => x.checked).map((x) => x.id);
    for (var i = 0; i < selectedProjectDesignations.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          'Project Designation',
          selectedProjectDesignations[i].name
        ),
      );
    }

    if (this.kycTag) {
      var kycTag = this.kycTagOptions.find((s) => s.value == this.kycTag);
      this.selectedFilters.push(new ChecklistItem('Tag', kycTag!.name));
    }

    if (this.selectedApplicationStage) {
      var applicationStage = this.applicationStageOptions.find(
        (s) => s.value == this.selectedApplicationStage,
      );
      this.selectedFilters.push(
        new ChecklistItem('Application Stage', applicationStage!.displayValue),
      );
    }

    let selectedOrganizations = this.organizations.filter((x) => x.checked);
    this.selectedOrganizations = this.organizations.filter((x) => x.checked).map((x) => x.id);
    for (var i = 0; i < selectedOrganizations.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          'Assigned Vendor',
          selectedOrganizations[i].name!,
          'NEW_SERVICE.ASSIGNED_VENDOR',
        ),
      );
    }

    if (this.selectedAssignStatus) {
      var assignStatus = this.assignStatusOptions.find(
        (s) => s.value == this.selectedAssignStatus,
      );
      this.selectedFilters.push(
        new ChecklistItem('Assignment Status', assignStatus!.displayValue),
      );
    }
  }

  removeFilter(item: ChecklistItem) {
    if (item.property == 'Search') {
      this.searchText = '';
    }
    else if (item.property == this.buName) {
      this.regions.forEach((region) => {
        if (region.name === item.selectedValue) {
          region.checked = false;
        }
      });
      this.selectRegion();
      this.clicked = !this.clicked;
    } 
    else if (item.property == this.utName) {
      this.areas.forEach((area) => {
        if (area.name === item.selectedValue) {
          area.checked = false;
        }
      });
      this.clicked = !this.clicked;
    }
    else if (item.property == 'Geographical Region') {
      this.geographicalRegions.forEach((region) => {
        if (region.name === item.selectedValue) {
          region.checked = false;
        }
      });
      this.selectGeographicalRegion();
      this.clicked = !this.clicked;
    }
    else if (item.property == 'Sub Geographical Region') {
      this.subGeographicalRegions.forEach((sub) => {
        if (sub.name === item.selectedValue) {
          sub.checked = false;
        }
      });
      this.clicked = !this.clicked;
    }
    else if (item.property == 'Service Type') {
      this.selectedSltStatus = undefined;
      this.showSubserviceTypes = false;
      this.selectedSubserviceType = undefined;
    } 
    else if (item.property == 'Service Request Type') {
      this.selectedSubserviceType = undefined;
    }
    else if (item.property == 'Project Status') {
      this.projectTypeOptions.forEach((pt) => {
        if (pt.name === item.selectedValue) {
          pt.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } 
    else if (item.property == 'Project Designation') {
      (this.projectDesignations as any[]).forEach((pd) => {
        if (pd.name === item.selectedValue) {
          pd.checked = false;
        }
      });
      this.clicked = !this.clicked;
    }
    else if (item.property === 'Tag') {
      this.kycTag = undefined;
    } 
    else if (item.property === 'Application Stage') {
      this.selectedApplicationStage = undefined;
    }
    else if (item.property == 'Assigned Vendor') {
      this.organizations.forEach((org) => {
        if (org.name === item.selectedValue) {
          org.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } 
    else if (item.property == 'Assignment Status') {
      this.selectedAssignStatus = undefined;
    }

    this.createFilterCards();

    if (this.selectedFilters.length == 0) {
      this.regions.forEach((region) => {
        region.checked = false;
      });
      this.geographicalRegions.forEach((region) => {
        region.checked = false;
      });
      this.currentPage = 1;
      this.reloadTable();
    }
  }

  resetFilter(): void {
    this.selectedFilters = [];

    this.searchText = '';

    this.regions.forEach((region) => (region.checked = false));
    this.areas.forEach((area) => (area.checked = false));
    this.areas = [];
    this.selectedAreas = [];
    this.selectedRegions = [];

    this.geographicalRegions.forEach((region) => (region.checked = false));
    this.subGeographicalRegions.forEach((sub) => (sub.checked = false));
    this.subGeographicalRegions = [];
    this.selectedSubGeographicalRegions = [];

    this.clicked = !this.clicked;

    this.selectedSltStatus = undefined;

    this.selectedSubserviceType = undefined;

    this.selectedProjectTypes = [];
    this.projectTypeOptions.forEach((pt) => (pt.checked = false));

    this.selectedProjectDesignationIds = [];
    (this.projectDesignations as any[]).forEach((pd) => (pd.checked = false));

    this.kycTag = undefined;
  
    this.selectedApplicationStage = undefined;

    this.selectedOrganizations = [];
    this.organizations.forEach((org) => (org.checked = false));

    this.selectedAssignStatus = undefined;

    this.currentPage = 1;
    this.reloadTable();
  }

  applyFilter(): void {
    this.currentPage = 1;
    this.reloadTable();
  }

  pageChange(value: number): void {
    this.currentPage = value;
    this.reloadTable();
  }

  @HostListener('document:click')
  onClickOutside(): void {
    this.showSelectSearchType = false;
  }

  private onAnyScroll = (): void => {
    this.showSelectSearchType = false;
  };

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.searchTextUpdate.complete();
    document.removeEventListener('scroll', this.onAnyScroll, true);
  }
}